import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import {
  QuickFilterOption,
  QuickFilterToggleGroup,
} from "../../../common/components/functional/QuickFilterToggleGroup/QuickFilterToggleGroup";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import { TalentQuickFilterButton } from "../../dashboard/talents/types";
import { selectRecruitmentAreaOptions } from "../../settings/recruitment/recruitmentsSlice";
import {
  TalentStatus,
  TalentFilterValues,
  DEFAULT_TALENT_FILTER_VALUES,
} from "../types/common";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

type Props = {
  selectedQuickFilter: TalentQuickFilterButton | "";
  onQuickFilterChange: (
    quickFilter: TalentQuickFilterButton | "",
    filters: SearchFilter[],
  ) => void;
};

const TalentQuickFilterButtons2 = ({
  selectedQuickFilter,
  onQuickFilterChange,
}: Props) => {
  const { t } = useTranslation([
    "dashboard",
    "talentProfile",
    "common",
    "order",
    "workHistory",
  ]);
  const { counts } = useAppSelector((state) => state.dashboard.talentCounts);
  const { id: userId, fullName } = useCurrentUser();
  const recruitmentAreaOptions = useAppSelector(selectRecruitmentAreaOptions);

  const getQuickFilterPreset = (
    filterType: TalentQuickFilterButton,
  ): SearchFilter[] => {
    const ownNewTalentStatuses = [
      TalentStatus.INCOMPLETE,
      TalentStatus.NEW,
      TalentStatus.INTERVIEW_PENDING,
      TalentStatus.INTERVIEW,
    ];
    const teamNewTalentStatuses = [TalentStatus.INCOMPLETE, TalentStatus.NEW];
    const teamRecruitmentAreas = recruitmentAreaOptions
      .filter((option) => option.isTeamArea)
      .map((area) => ({ id: area.id, name: area.name }));

    let presetValues: TalentFilterValues;

    switch (filterType) {
      case TalentQuickFilterButton.OWN_NEW:
        presetValues = {
          ...DEFAULT_TALENT_FILTER_VALUES,
          status: ownNewTalentStatuses,
          hrRepresentative: [userId],
        };
        break;

      case TalentQuickFilterButton.TEAM_NEW:
        presetValues = {
          ...DEFAULT_TALENT_FILTER_VALUES,
          status: teamNewTalentStatuses,
          hrRepresentative: ["noHrRep"],
          areas: teamRecruitmentAreas.map((a) => a.id),
        };
        break;

      case TalentQuickFilterButton.OWN_ENDING_SOON:
        presetValues = {
          ...DEFAULT_TALENT_FILTER_VALUES,
          orderQueues: ["own"],
          hrRepresentative: [userId],
        };
        break;

      case TalentQuickFilterButton.TEAM_ENDING_SOON:
        presetValues = {
          ...DEFAULT_TALENT_FILTER_VALUES,
          orderQueues: ["own"],
        };
        break;

      default:
        presetValues = DEFAULT_TALENT_FILTER_VALUES;
    }

    // Build complete SearchFilter[] array (matching TalentFilters sendFilters structure)
    return [
      {
        name: "status",
        value: presetValues.status,
        displayName: t("talentProfile:statusLabel"),
      },
      {
        name: "hrRepresentative",
        value: presetValues.hrRepresentative,
        displayName: t("talentProfile:myHr"),
        displayValue:
          filterType === TalentQuickFilterButton.OWN_NEW ||
          filterType === TalentQuickFilterButton.OWN_ENDING_SOON
            ? fullName
            : filterType === TalentQuickFilterButton.TEAM_NEW
              ? t("talentProfile:noHrRep")
              : "",
      },
      {
        name: "talentTags",
        value: presetValues.talentTags,
        displayName: t("talentProfile:talentTag"),
      },
      {
        name: "areas",
        value: presetValues.areas,
        displayName: t("common:area"),
        displayValue:
          filterType === TalentQuickFilterButton.TEAM_NEW
            ? teamRecruitmentAreas.map((a) => a.name).join(", ")
            : "",
        ...(filterType === TalentQuickFilterButton.TEAM_NEW && {
          lookupValues: teamRecruitmentAreas,
        }),
      },
      {
        name: "orderQueues",
        value: presetValues.orderQueues,
        displayName: t("order:team"),
        displayValue:
          filterType === TalentQuickFilterButton.OWN_ENDING_SOON ||
          filterType === TalentQuickFilterButton.TEAM_ENDING_SOON
            ? t("talentProfile:own")
            : "",
      },
      {
        name: "workTitleIds",
        value: presetValues.workTitleIds,
        displayName: t("talentProfile:workTitle"),
      },
      {
        name: "skillIds",
        value: presetValues.skillIds,
        displayName: t("talentProfile:skills"),
      },
      {
        name: "city",
        value: presetValues.city,
        displayName: t("workHistory:city"),
      },
      {
        name: "certificateIds",
        value: presetValues.certificateIds,
        displayName: t("talentProfile:workPermitsTitle"),
      },
      {
        name: "trainingIds",
        value: presetValues.trainingIds,
        displayName: t("common:trainings"),
      },
      {
        name: "languageSkillIds",
        value: presetValues.languageSkillIds,
        displayName: t("talentProfile:languageSkills"),
      },
      {
        name: "vehicleAvailable",
        value: presetValues.vehicleAvailable,
        displayName: t("talentProfile:vehicleAvailableShort"),
      },
      {
        name: "customerOrgId",
        value: presetValues.customerOrgId,
        displayName: t("talentProfile:customer"),
      },
      {
        name: "assignmentType",
        value: presetValues.assignmentType,
        displayName: t("talentProfile:assignmentType"),
      },
      {
        name: "assignmentActiveFrom",
        value: presetValues.assignmentActiveFrom,
        displayName: `${t("talentProfile:assignmentActive")} ${t("talentProfile:from").toLowerCase()}`,
      },
      {
        name: "assignmentActiveTo",
        value: presetValues.assignmentActiveTo,
        displayName: `${t("talentProfile:assignmentActive")} ${t("talentProfile:to").toLowerCase()}`,
      },
      {
        name: "assignmentInactiveFrom",
        value: presetValues.assignmentInactiveFrom,
        displayName: `${t("talentProfile:assignmentInactive")} ${t("talentProfile:from").toLowerCase()}`,
      },
      {
        name: "assignmentInactiveTo",
        value: presetValues.assignmentInactiveTo,
        displayName: `${t("talentProfile:assignmentInactive")} ${t("talentProfile:to").toLowerCase()}`,
      },
    ];
  };

  const quickFilterOptions: QuickFilterOption<TalentQuickFilterButton>[] = [
    {
      value: TalentQuickFilterButton.OWN_NEW,
      label: t("talents.ownNew"),
      count: counts?.talentsAssignedToMeCount,
    },
    {
      value: TalentQuickFilterButton.TEAM_NEW,
      label: t("talents.teamsNew"),
      count: counts?.teamsNewTalentCount,
    },
    {
      value: TalentQuickFilterButton.OWN_ENDING_SOON,
      label: t("talents.ownEndingSoon"),
      count: counts?.talEmpEndingSoonAssignedToMeCount,
    },
    {
      value: TalentQuickFilterButton.TEAM_ENDING_SOON,
      label: t("talents.teamsEndingSoon"),
      count: counts?.talEmpEndingSoonAssignedToTeamCount,
    },
  ];

  const handleChange = (newValue: TalentQuickFilterButton | "") => {
    if (newValue === "") {
      // Clear all filters - use getQuickFilterPreset with default to get empty filters
      const emptyFilters = getQuickFilterPreset(TalentQuickFilterButton.EMPTY);
      onQuickFilterChange("", emptyFilters);
    } else {
      const preset = getQuickFilterPreset(newValue);
      onQuickFilterChange(newValue, preset);
    }
  };

  return (
    <QuickFilterToggleGroup<TalentQuickFilterButton>
      options={quickFilterOptions}
      value={selectedQuickFilter}
      onChange={handleChange}
    />
  );
};

export default TalentQuickFilterButtons2;
